import React from "react";

import FlightCard from "./FlightCard";
import FlightListSkeleton from "./FlightListSkeleton";

/**
 * FlightListByDate Component
 * Groups and displays flights by date with infinite scroll support
 * 
 * @param {Object} props
 * @param {Object} props.flightsByDate - Flights grouped by date
 * @param {Function} [props.onLoadMore] - Callback to load more flights
 * @param {boolean} [props.isLoading] - Whether more flights are being loaded
 * @param {boolean} [props.hasMore] - Whether there are more flights to load
 * @param {Object} [props.infiniteScrollRef] - Ref for the load-more trigger element (from useInfiniteScroll)
 */
const FlightListByDate = ({ flightsByDate, onLoadMore, isLoading, hasMore, infiniteScrollRef }) => {
  if (isLoading && (!flightsByDate || Object.keys(flightsByDate).length === 0)) {
    return <FlightListSkeleton groups={3} cardsPerGroup={2} />;
  }

  if (!flightsByDate || Object.keys(flightsByDate).length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No flights scheduled</p>
      </div>
    );
  }

  const sortedDates = Object.keys(flightsByDate).sort((a, b) => {
    return new Date(a) - new Date(b);
  });

  return (
    <div className="space-y-6">
      {sortedDates.map((dateKey) => {
        const flights = flightsByDate[dateKey];
        // Parse YYYY-MM-DD parts directly to avoid timezone re-interpretation
        const [year, month, day] = dateKey.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const monthDay = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        const dayName = date.toLocaleDateString("en-US", {
          weekday: "long",
        });

        const today = new Date();
        const isToday =
          date.getDate() === today.getDate() &&
          date.getMonth() === today.getMonth() &&
          date.getFullYear() === today.getFullYear();

        return (
          <div key={dateKey}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {monthDay}
              </h2>
              <span
                className={`
                  px-2 py-1 text-xs font-medium rounded-lg
                  ${
                    isToday
                      ? "bg-primary-500 text-white"
                      : "bg-gray-100 text-gray-600"
                  }
                `}
              >
                {dayName}
              </span>
            </div>

            <div>
              {flights.map((flight) => (
                <FlightCard key={flight.id} flight={flight} />
              ))}
            </div>
          </div>
        );
      })}
      
      {hasMore && (
        <div 
          ref={infiniteScrollRef}
          className="py-4"
        >
          {isLoading && <FlightListSkeleton groups={1} cardsPerGroup={2} />}
        </div>
      )}
    </div>
  );
};

export default FlightListByDate;
